import React, { createContext, useContext, useEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/api/supabaseClient';
import {
  getLocalConsent,
  getOrCreateAnonymousId,
  saveLocalConsent,
  initializeTrackers,
} from './consentManager';

const ConsentContext = createContext(null);

const SYNC_KEY_PREFIX = 'tresopilot_cookie_consent_sync_';

function consentFingerprint(consent) {
  if (!consent) return '';
  const a = consent.analytics === true ? '1' : '0';
  const m = consent.marketing === true ? '1' : '0';
  const v = consent.consent_version || '';
  const e = consent.expires_at || '';
  return `${a}|${m}|${v}|${e}`;
}

function computeConsentStatus(analytics, marketing) {
  if (analytics && marketing) return 'accepted_all';
  if (!analytics && !marketing) return 'rejected_all';
  return 'custom';
}

async function getLatestDbConsent(userId) {
  try {
    const consents = await base44.entities.CookieConsent.filter({ user_id: userId }, '-created_date', 1);
    return consents?.[0] || null;
  } catch (_) {
    return null;
  }
}

async function syncLocalConsentToDb(userId) {
  if (typeof window === 'undefined') return;

  const localConsent = getLocalConsent();
  if (!localConsent) return;

  // Do not keep syncing the same consent again and again
  const fpLocal = consentFingerprint(localConsent);
  const syncKey = `${SYNC_KEY_PREFIX}${userId}`;
  const lastSynced = localStorage.getItem(syncKey);
  if (lastSynced === fpLocal) return;

  // If DB already has the same consent, mark as synced and stop
  const latestDb = await getLatestDbConsent(userId);
  const fpDb = latestDb ? consentFingerprint(latestDb) : '';
  if (fpDb && fpDb === fpLocal) {
    localStorage.setItem(syncKey, fpLocal);
    return;
  }

  // Persist consent for cross-device reuse
  const analytics = localConsent.analytics === true;
  const marketing = localConsent.marketing === true;

  const payload = {
    anonymous_id: getOrCreateAnonymousId(),
    necessary: true,
    analytics,
    marketing,
    consent_status: computeConsentStatus(analytics, marketing),
    consent_version: localConsent.consent_version || '1.0',
    expires_at: localConsent.expires_at,
    updated_at: localConsent.updated_at || new Date().toISOString(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    sync_source: 'login_auto',
  };

  try {
    await base44.entities.CookieConsent.create(payload);
    localStorage.setItem(syncKey, fpLocal);
  } catch (e) {
    // If DB insert fails (RLS not configured), we keep local consent anyway.
    console.log('[Consent] DB sync skipped:', e?.message || e);
  }
}

export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return context;
}

export function ConsentProvider({ children }) {
  const [hasConsent, setHasConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkConsent = async () => {
    setIsLoading(true);
    try {
      const localConsent = getLocalConsent();

      // If local consent exists and is not expired => apply
      if (localConsent && (!localConsent.expires_at || new Date(localConsent.expires_at) > new Date())) {
        setHasConsent(true);
        initializeTrackers();

        // If user is logged in, persist to DB once (cross-device)
        try {
          const user = await base44.auth.me();
          if (user?.id) await syncLocalConsentToDb(user.id);
        } catch (_) {}

        return;
      }

      // No local consent (or expired): try to load from DB if logged in
      try {
        const user = await base44.auth.me();
        if (user?.id) {
          const dbConsent = await getLatestDbConsent(user.id);
          if (dbConsent && (!dbConsent.expires_at || new Date(dbConsent.expires_at) > new Date())) {
            const preferences = {
              analytics: dbConsent.analytics,
              marketing: dbConsent.marketing,
            };

            // Restore exact version/expiry so consent is consistent across devices
            saveLocalConsent(preferences, {
              consent_version: dbConsent.consent_version,
              expires_at: dbConsent.expires_at,
              updated_at: dbConsent.updated_at || dbConsent.created_date,
            });

            // Mark as synced to prevent immediate re-insert
            if (typeof window !== 'undefined') {
              const fpDb = consentFingerprint(dbConsent);
              localStorage.setItem(`${SYNC_KEY_PREFIX}${user.id}`, fpDb);
            }

            setHasConsent(true);
            initializeTrackers();
            return;
          }
        }
      } catch (_) {
        // Not logged in (or DB unavailable): do nothing
      }

      setHasConsent(false);
    } catch (error) {
      console.error('Error checking consent:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkConsent();

    // On first login, if a local consent exists, push it to DB (cross-device)
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user?.id) {
        // Run sync + re-check (non-blocking)
        syncLocalConsentToDb(session.user.id);
        checkConsent();
      }
    });

    return () => {
      data?.subscription?.unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    hasConsent,
    isLoading,
    refreshConsent: checkConsent,
  };

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}
